<script setup lang="ts">
import { useComputedAttrs, useComputedInput, useEventsToValidate } from './helpers'
import { type CheckboxInputProps } from './types'

const props = defineProps<CheckboxInputProps>()
const emit = defineEmits(['update:modelValue', 'change', 'blur', 'validate', 'invalid'])
const input = useComputedInput(props, emit)
const attrs = useComputedAttrs(props, 'checkbox')
const events = useEventsToValidate(emit)

function setIndeterminate(el: HTMLInputElement, value: unknown) {
  el.indeterminate = Boolean(value)
}

const vIndeterminate = {
  mounted: (el: HTMLInputElement, { value }: { value: unknown }) => setIndeterminate(el, value),
  updated: (el: HTMLInputElement, { value }: { value: unknown }) => setIndeterminate(el, value),
}
</script>

<template>
  <input
    v-model="input"
    v-autofocus="props.autofocus"
    v-bind="attrs"
    v-indeterminate="props.indeterminate"
    type="checkbox"
    :aria-invalid="props.invalid"
    :aria-checked="props.indeterminate ? 'mixed' : undefined"
    :tabindex="props.tabindex"
    v-on="events"
    :required="props.required"
  />
</template>

<style scoped>
input {
  /* Size in em so it scales with font-size; FormInput sets font-size on .input-wrapper input[type='checkbox'] from the size class. */
  appearance: none;
  -webkit-appearance: none;
  margin: 0;
  width: 1.5em;
  height: 1.5em;
  flex-shrink: 0;
  border: 1px solid var(--form-checkbox-off-border);
  border-radius: var(--form-checkbox-border-radius);
  background: var(--form-checkbox-off-bg);
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    background var(--transition-duration) ease,
    border-color var(--transition-duration) ease;

  &:hover:not(:disabled) {
    background: var(--form-checkbox-off-bg-hover);
    border-color: var(--form-checkbox-off-border-hover);
  }

  &:focus {
    outline: none;
    background: var(--form-checkbox-off-bg-focus);
    border-color: var(--form-checkbox-off-border-focus);
  }

  &:focus-visible {
    outline: none;
    box-shadow:
      0 0 0 4px var(--form-input-border-focus-visible),
      0 0 0 6px var(--form-input-border-focus-visible-outline);
  }

  &:disabled {
    background: var(--form-checkbox-off-bg-disabled);
    border-color: var(--form-checkbox-off-border-disabled);
    cursor: default;
  }

  &:checked:not(:indeterminate) {
    background: var(--form-checkbox-on-bg);
    border-color: var(--form-checkbox-on-border);

    &:hover:not(:disabled) {
      background: var(--form-checkbox-on-bg-hover);
      border-color: var(--form-checkbox-on-border-hover);
    }

    &:focus {
      background: var(--form-checkbox-on-bg-focus);
      border-color: var(--form-checkbox-on-border-focus);

      &::after {
        border-color: var(--form-checkbox-on-icon-focus);
      }
    }

    &:focus-visible {
      box-shadow:
        0 0 0 4px var(--form-input-border-focus-visible),
        0 0 0 6px var(--form-input-border-focus-visible-outline);
    }

    &::after {
      content: '';
      width: 24%;
      height: 45%;
      top: -5%;
      position: relative;
      border-bottom: 2px solid var(--form-checkbox-on-icon);
      border-right: 2px solid var(--form-checkbox-on-icon);
      transform: rotate(45deg);
      transform-origin: center center;
    }

    &:disabled {
      background: var(--form-checkbox-on-bg-disabled);
      border-color: var(--form-checkbox-on-border-disabled);

      &::after {
        border-bottom-color: var(--form-checkbox-on-icon-disabled);
        border-right-color: var(--form-checkbox-on-icon-disabled);
      }
    }
  }

  &:indeterminate {
    background: var(--form-checkbox-indeterminate-bg);
    border-color: var(--form-checkbox-indeterminate-border);

    &:hover:not(:disabled) {
      background: var(--form-checkbox-indeterminate-bg-hover);
      border-color: var(--form-checkbox-indeterminate-border-hover);
    }

    &:focus {
      background: var(--form-checkbox-indeterminate-bg-focus);
      border-color: var(--form-checkbox-indeterminate-border-focus);
    }

    &:focus-visible {
      box-shadow:
        0 0 0 4px var(--form-input-border-focus-visible),
        0 0 0 6px var(--form-input-border-focus-visible-outline);
    }

    &::after {
      content: '';
      position: absolute;
      left: 50%;
      top: 50%;
      width: 55%;
      height: 2px;
      background: var(--form-checkbox-indeterminate-icon);
      transform: translate(-50%, -50%);
    }

    &:disabled {
      background: var(--form-checkbox-indeterminate-bg-disabled);
      border-color: var(--form-checkbox-indeterminate-border-disabled);

      &::after {
        background: var(--form-checkbox-indeterminate-icon-disabled);
      }
    }
  }
}
</style>
